import React, { useState } from 'react';
import { StudentProgress } from '../types';
import { quizSections } from '../data/questions';
import { evaluateAnswer } from '../utils/evaluation';
import SectionNav from './SectionNav';
import NotesModal from './NotesModal';
import EnhancedSectionView from './EnhancedSectionView';
import '../utils/testing'; // Load testing utilities for development

interface QuizLayoutProps {
  progress: StudentProgress;
  onProgressUpdate: (progress: StudentProgress) => void;
  onQuizEvaluated: (progress: StudentProgress) => void;
  onReset: () => void;
}

const QuizLayout: React.FC<QuizLayoutProps> = ({ progress, onProgressUpdate, onQuizEvaluated, onReset }) => {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const currentSection = quizSections[progress.currentSection];
  const currentSectionProgress = progress.sections[progress.currentSection];

  // Calculate section stats
  const visibleQuestions = currentSection.questions.filter(question => {
    const previousAnswer = currentSectionProgress?.answers.find(a => a.questionId === question.id);
    return !previousAnswer || !previousAnswer.isCorrect;
  });

  const isQuestionAnswered = (questionId: string) => {
    const answers: { [questionId: string]: any } = {};
    currentSectionProgress?.answers.forEach(answer => {
      if (answer.type === 'multiple-choice') {
        answers[answer.questionId] = answer.selectedOption;
      } else if (answer.type === 'free-response') {
        answers[answer.questionId] = answer.freeResponse || '';
      }
    });
    return answers[questionId] !== undefined && answers[questionId] !== null &&
           (typeof answers[questionId] === 'string' ? answers[questionId].trim() !== '' : true);
  };

  const scrollToFirstUnanswered = () => {
    const firstUnansweredIndex = visibleQuestions.findIndex(q => !isQuestionAnswered(q.id));
    if (firstUnansweredIndex >= 0) {
      const questionId = visibleQuestions[firstUnansweredIndex].id;
      const element = document.getElementById(`question-${questionId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const allQuestionsAnswered = () => {
    return quizSections.every(section => {
      const sectionProgress = progress.sections.find(s => s.id === section.id);
      return sectionProgress && (sectionProgress.answers || []).length === section.questions.length;
    });
  };

  const allQuestionsCorrect = () => {
    return quizSections.every(section => {
      const sectionProgress = progress.sections.find(s => s.id === section.id);
      return sectionProgress && (sectionProgress.answers || []).length === section.questions.length &&
        (sectionProgress.answers || []).every(answer => answer.isCorrect);
    });
  };

  const handleSectionSelect = (sectionIndex: number) => {
    const updatedProgress = {
      ...progress,
      currentSection: sectionIndex,
    };
    onProgressUpdate(updatedProgress);
  };

  const handleNextSection = () => {
    if (progress.currentSection < quizSections.length - 1) {
      handleSectionSelect(progress.currentSection + 1);
    }
  };

  const findFirstUnansweredQuestion = () => {
    for (let sectionIndex = 0; sectionIndex < quizSections.length; sectionIndex++) {
      const section = quizSections[sectionIndex];
      const sectionProgress = progress.sections[sectionIndex];

      for (const question of section.questions) {
        const existingAnswer = sectionProgress?.answers.find(a => a.questionId === question.id);
        if (!existingAnswer ||
            (question.type === 'multiple-choice' && existingAnswer.selectedOption === undefined) ||
            (question.type === 'free-response' && (!existingAnswer.freeResponse || existingAnswer.freeResponse.trim() === ''))) {
          return { sectionIndex, questionId: question.id };
        }
      }
    }
    return null;
  };

  const handleGoToFirstUnanswered = () => {
    const firstUnanswered = findFirstUnansweredQuestion();
    if (firstUnanswered) {
      // Navigate to the section if it's different
      if (firstUnanswered.sectionIndex !== progress.currentSection) {
        handleSectionSelect(firstUnanswered.sectionIndex);
        // Wait for the section to load then scroll to question
        setTimeout(() => {
          const element = document.getElementById(`question-${firstUnanswered.questionId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        // Same section, just scroll to the question
        const element = document.getElementById(`question-${firstUnanswered.questionId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  const handleSectionComplete = () => {
    const currentSectionProgress = progress.sections[progress.currentSection];
    const allAnswered = (currentSectionProgress?.answers || []).length === currentSection.questions.length;
    const allCorrect = allAnswered && (currentSectionProgress?.answers || []).every(answer => answer.isCorrect);

    // Update section as completed
    const updatedSectionProgress = {
      ...currentSectionProgress,
      completed: true,
      passed: allCorrect,
      score: allCorrect ? 1.0 : ((currentSectionProgress?.answers || []).filter(a => a.isCorrect).length / currentSection.questions.length),
      completedAt: new Date(),
    };

    const updatedProgress = {
      ...progress,
      sections: progress.sections.map((section, index) =>
        index === progress.currentSection ? updatedSectionProgress : section
      ),
    };

    if (allCorrect) {
      // Move to next section after a delay if passed
      setTimeout(() => {
        const nextSection = progress.currentSection + 1;
        if (nextSection < quizSections.length) {
          const nextSectionProgress = {
            ...updatedProgress,
            currentSection: nextSection,
          };
          onProgressUpdate(nextSectionProgress);
        } else {
          // All sections completed
          onProgressUpdate(updatedProgress);
        }
      }, 2000);
    }

    onProgressUpdate(updatedProgress);
  };

  const handleSubmitQuiz = async () => {
    // Allow submission even if not all questions are answered
    const unansweredCount = quizSections.reduce((count, section) => {
      const sectionProgress = progress.sections.find(s => s.id === section.id);
      const answeredCount = sectionProgress?.answers.length || 0;
      return count + (section.questions.length - answeredCount);
    }, 0);

    if (unansweredCount > 0) {
      const proceed = confirm(`You have ${unansweredCount} unanswered questions. Unanswered questions will be marked as incorrect. Do you want to continue?`);
      if (!proceed) return;
    }

    setIsEvaluating(true);

    try {
      // Evaluate all questions
      const updatedSections = await Promise.all(
        progress.sections.map(async (sectionProgress) => {
          const section = quizSections.find(s => s.id === sectionProgress.id);
          if (!section) return sectionProgress;

          // Create a complete list of answers, including unanswered questions
          const allAnswers = section.questions.map(question => {
            const existingAnswer = sectionProgress.answers.find(a => a.questionId === question.id);
            if (existingAnswer) {
              return existingAnswer;
            } else {
              // Create a placeholder answer for unanswered questions
              return {
                questionId: question.id,
                type: question.type,
                selectedOption: question.type === 'multiple-choice' ? undefined : undefined,
                freeResponse: question.type === 'free-response' ? undefined : undefined,
                isCorrect: false,
                score: 0,
                attemptCount: 1,
              };
            }
          });

          const evaluatedAnswers = await Promise.all(
            allAnswers.map(async (answer) => {
              const question = section.questions.find(q => q.id === answer.questionId);
              if (!question) return answer;

              // If answer is already marked (unanswered placeholder), skip evaluation
              if (answer.selectedOption === undefined && answer.freeResponse === undefined) {
                return {
                  ...answer,
                  isCorrect: false,
                  score: 0,
                  feedback: 'Question not answered.',
                  attemptCount: 1,
                };
              }

              const evaluation = await evaluateAnswer(
                question,
                answer.selectedOption,
                answer.freeResponse
              );

              return {
                ...answer,
                isCorrect: evaluation.isCorrect,
                score: evaluation.score,
                feedback: evaluation.feedback,
                attemptCount: answer.attemptCount + 1,
              };
            })
          );

          const allCorrect = evaluatedAnswers.every(answer => answer.isCorrect);
          const avgScore = evaluatedAnswers.reduce((sum, answer) => sum + (answer.score || 0), 0) / evaluatedAnswers.length;

          return {
            ...sectionProgress,
            answers: evaluatedAnswers,
            completed: true,
            passed: allCorrect,
            score: avgScore,
            completedAt: new Date(),
          };
        })
      );

      const updatedProgress = {
        ...progress,
        sections: updatedSections,
      };

      // Start evaluation flow
      onQuizEvaluated(updatedProgress);
    } catch (error) {
      console.error('Error evaluating quiz:', error);
      alert('There was an error evaluating your quiz. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  if (!currentSection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Quiz Complete!</h2>
          <button onClick={onReset} className="btn-primary">
            Start New Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {currentSection.name}
              </h1>
              <p className="text-sm text-gray-600">{currentSection.description}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowNotesModal(true)}
                className="btn-secondary"
              >
                View Safety Notes
              </button>
              <button
                onClick={handleSubmitQuiz}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 bg-blue-600 hover:bg-blue-700 text-white`}
                disabled={isEvaluating}
              >
                {isEvaluating ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Evaluating Quiz...
                  </span>
                ) : (
                  'Submit Quiz'
                )}
              </button>
              <button onClick={onReset} className="btn-secondary">
                Reset Quiz
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Section Navigation */}
          <div className="lg:col-span-1">
            <SectionNav
              sections={quizSections}
              progress={progress}
              onSectionSelect={handleSectionSelect}
              onScrollToFirstUnanswered={scrollToFirstUnanswered}
            />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <EnhancedSectionView
              section={currentSection}
              progress={progress}
              onProgressUpdate={onProgressUpdate}
              onSectionComplete={handleSectionComplete}
              onNextSection={handleNextSection}
              onSubmitQuiz={handleSubmitQuiz}
              onGoToFirstUnanswered={handleGoToFirstUnanswered}
            />
          </div>
        </div>
      </div>

      {/* Notes Modal */}
      <NotesModal
        section={currentSection}
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
      />
    </div>
  );
};

export default QuizLayout;